//projectlab-web/src/components/ui/Card.tsx
import { HTMLAttributes, forwardRef, createElement } from 'react'
import { clsx } from 'clsx'

type CardTag = 'div' | 'article' | 'section' | 'li'

interface CardProps extends HTMLAttributes<HTMLElement> {
  hover?: boolean
  glow?: boolean
  padding?: 'none' | 'sm' | 'md' | 'lg'
  as?: CardTag
}

const paddingClasses = {
  none: '',
  sm: 'p-4',
  md: 'p-5',
  lg: 'p-6',
}

export const Card = forwardRef<HTMLElement, CardProps>(
  ({ hover, glow, padding = 'md', as = 'div', className, children, ...props }, ref) => {
    return createElement(
      as,
      {
        ...props,
        ref,
        className: clsx(
          'card',
          hover && 'card-hover cursor-pointer',
          glow && 'hover:shadow-glow',
          paddingClasses[padding],
          className
        ),
      },
      children
    )
  }
)

Card.displayName = 'Card'

export function CardHeader({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={clsx('flex items-start justify-between gap-3 mb-3', className)}
      {...props}
    >
      {children}
    </div>
  )
}

export function CardTitle({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3
      className={clsx(
        'font-display font-semibold text-base text-[var(--color-text)] leading-snug',
        className
      )}
      {...props}
    >
      {children}
    </h3>
  )
}

export function CardBody({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={clsx(
        'text-sm text-[var(--color-text-muted)] leading-relaxed',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

export function CardFooter({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={clsx(
        'mt-4 pt-4 border-t border-[var(--color-divider)] flex items-center justify-between gap-3',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}
